
import { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CalendarIcon, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface InterviewSchedulerProps {
  candidateName: string;
  candidateEmail: string;
  onClose: () => void;
}

const InterviewScheduler = ({ candidateName, candidateEmail, onClose }: InterviewSchedulerProps) => {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState('');
  const [platform, setPlatform] = useState('google-meet');
  const [meetingLink, setMeetingLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Times from 9am to 5pm in 30-minute increments
  const timeSlots = Array.from({ length: 17 }, (_, i) => {
    const hour = Math.floor(i / 2) + 9;
    const minute = i % 2 === 0 ? '00' : '30';
    const amPm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${hour12}:${minute} ${amPm}`;
  });

  const handleSubmit = async () => {
    if (!date) {
      toast.error('Please select a date');
      return;
    }

    if (!time) {
      toast.error('Please select a time');
      return;
    }

    if (platform === 'custom' && !meetingLink) {
      toast.error('Please enter a meeting link');
      return;
    }

    setIsSubmitting(true);

    try {
      // In a real application, this would make an API call to send an email
      // For this example, we'll simulate the email sending with a timeout
      await new Promise(resolve => setTimeout(resolve, 1500));

      const formattedDate = format(date, 'EEEE, MMMM do, yyyy');
      
      let platformName = '';
      let finalMeetingLink = meetingLink;
      
      switch (platform) {
        case 'google-meet':
          platformName = 'Google Meet';
          finalMeetingLink = 'https://meet.google.com/sample-link';
          break;
        case 'microsoft-teams':
          platformName = 'Microsoft Teams';
          finalMeetingLink = 'https://teams.microsoft.com/sample-link';
          break;
        case 'zoom':
          platformName = 'Zoom';
          finalMeetingLink = 'https://zoom.us/j/sample-link';
          break;
        case 'custom':
          platformName = 'Custom Link';
          break;
      }

      toast.success(`Interview scheduled for ${formattedDate} at ${time}`);
      toast.success(`Email sent to ${candidateEmail}`);
      console.log('Interview details:', {
        candidateName,
        candidateEmail,
        date: formattedDate,
        time,
        platform: platformName,
        meetingLink: finalMeetingLink
      });

      onClose();
    } catch (error) {
      console.error('Error scheduling interview:', error);
      toast.error('Failed to schedule interview. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 p-4 max-w-md mx-auto">
      <h3 className="text-lg font-medium">Schedule Interview with {candidateName}</h3>
      <div className="grid gap-4">
        <div className="space-y-2">
          <Label>Select Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, 'PPP') : <span>Select date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                initialFocus
                disabled={(date) => date < new Date()} // Disable past dates
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Select Time</Label>
          <Select onValueChange={setTime}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select time">
                {time ? (
                  <div className="flex items-center">
                    <Clock className="mr-2 h-4 w-4" />
                    <span>{time}</span>
                  </div>
                ) : (
                  <span>Select time</span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {timeSlots.map((slot) => (
                <SelectItem key={slot} value={slot}>
                  {slot}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Meeting Platform</Label>
          <RadioGroup
            defaultValue="google-meet"
            onValueChange={setPlatform}
            className="grid grid-cols-2 gap-4"
          >
            <div className="flex items-center space-x-2 rounded-md border p-2">
              <RadioGroupItem value="google-meet" id="google-meet" />
              <Label htmlFor="google-meet" className="cursor-pointer">Google Meet</Label>
            </div>
            <div className="flex items-center space-x-2 rounded-md border p-2">
              <RadioGroupItem value="microsoft-teams" id="microsoft-teams" />
              <Label htmlFor="microsoft-teams" className="cursor-pointer">Microsoft Teams</Label>
            </div>
            <div className="flex items-center space-x-2 rounded-md border p-2">
              <RadioGroupItem value="zoom" id="zoom" />
              <Label htmlFor="zoom" className="cursor-pointer">Zoom</Label>
            </div>
            <div className="flex items-center space-x-2 rounded-md border p-2">
              <RadioGroupItem value="custom" id="custom" />
              <Label htmlFor="custom" className="cursor-pointer">Custom Link</Label>
            </div>
          </RadioGroup>
        </div>

        {platform === 'custom' && (
          <div className="space-y-2">
            <Label>Meeting Link</Label>
            <Input
              placeholder="https://..."
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
            />
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <div className="flex items-center">
                <span className="mr-2">Scheduling...</span>
                <div className="animate-spin h-4 w-4 border-2 border-white border-opacity-50 border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Schedule & Send Invite
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InterviewScheduler;
